import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core'
import { MessageService } from 'primeng/api'

import { MODE_OPTIONS, MODE_UI, PERSON_TYPE_OPTIONS, PREOFFER_LIMITS, ThresholdsForm, thresholdErrors, thresholdsChanged, thresholdsOf, versionSummary } from './preoffer-settings.view'
import { PreofferMode, PreofferSettingsOverview } from '@data/interfaces/preoffer.interface'
import { ApiPreofferService } from '@data/services/modules/preoffer/api-preoffer.service'
import { SnapshotResponse } from '@data/interfaces/verification-snapshot.interface'
import { AuthService } from '@data/services/modules/auth/auth.service'

/**
 * Configuración de la preoferta (plan v4.3 §8.1 y §9.4, D-62): Admin y Finanzas. Umbrales y modo se guardan por separado
 * porque piden permisos distintos (`update` y `activate`); cada cambio crea una versión con su motivo. Encender pide
 * motivo y una casilla con cuántas estarían hoy en «habría salido». Pasar a encendida no envía nada retroactivo.
 */
@Component({
  selector: 'app-preoffer-settings',
  templateUrl: './preoffer-settings.component.html',
  styleUrls: ['./preoffer-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class PreofferSettingsComponent implements OnInit {
  overview: PreofferSettingsOverview | null = null
  loading = false
  loadError: string | null = null

  form: ThresholdsForm | null = null
  thresholdsReason = ''
  savingThresholds = false
  //* Los errores se muestran después del primer intento de guardar, no mientras se escribe.
  showThresholdErrors = false

  modeTarget: PreofferMode = 'shadow'
  modeReason = ''
  acknowledged = false
  savingMode = false

  readonly modeUi = MODE_UI
  readonly modeOptions = MODE_OPTIONS
  readonly personTypeOptions = PERSON_TYPE_OPTIONS
  readonly limits = PREOFFER_LIMITS
  readonly versionSummary = versionSummary

  private readonly auth = inject(AuthService)
  readonly canUpdate = this.auth.hasModulePermission('lease-preoffer-settings', 'update')
  readonly canActivate = this.auth.hasModulePermission('lease-preoffer-settings', 'activate')

  private readonly api = inject(ApiPreofferService)
  private readonly cd = inject(ChangeDetectorRef)
  private readonly messages = inject(MessageService)

  ngOnInit(): void {
    this.load()
  }

  get currentMode(): PreofferMode | null {
    return this.overview?.active.mode ?? null
  }

  get thresholdsDirty(): boolean {
    return !!this.form && !!this.overview && thresholdsChanged(this.form, this.overview.active)
  }

  get thresholdErrors(): string[] {
    return this.form ? thresholdErrors(this.form, this.thresholdsReason) : []
  }

  get modeChanging(): boolean {
    return !!this.currentMode && this.modeTarget !== this.currentMode
  }

  get modeReady(): boolean {
    return this.modeChanging && this.modeReason.trim().length >= PREOFFER_LIMITS.reason && (this.modeTarget !== 'live' || this.acknowledged)
  }

  load(): void {
    this.loading = true
    this.loadError = null
    this.api.settings().subscribe((response) => {
      this.loading = false
      if (response.error) this.loadError = response.status === 403 ? 'No tienes permiso para ver la configuración de la preoferta.' : response.msg
      else this.apply(response.data!)
      this.cd.markForCheck()
    })
  }

  resetThresholds(): void {
    if (!this.overview) return
    this.form = thresholdsOf(this.overview.active)
    this.thresholdsReason = ''
    this.showThresholdErrors = false
  }

  saveThresholds(): void {
    if (!this.form || !this.canUpdate || this.savingThresholds) return
    this.showThresholdErrors = true
    if (this.thresholdErrors.length || !this.thresholdsDirty) return
    this.savingThresholds = true
    this.api.updateThresholds({ ...this.form, changeReason: this.thresholdsReason.trim() }).subscribe((response) => {
      this.savingThresholds = false
      this.afterSave(response, 'Umbrales guardados', 'Las evaluaciones que sigan usan la versión nueva; las ya decididas no cambian.')
    })
  }

  selectMode(mode: PreofferMode): void {
    this.modeTarget = mode
    this.acknowledged = false
  }

  saveMode(): void {
    if (!this.modeReady || !this.canActivate || this.savingMode) return
    this.savingMode = true
    const body = { mode: this.modeTarget, changeReason: this.modeReason.trim(), ...(this.modeTarget === 'live' ? { acknowledged: true } : {}) }
    this.api.changeMode(body).subscribe((response) => {
      this.savingMode = false
      this.afterSave(
        response,
        `Preoferta ${MODE_UI[body.mode].label.toLowerCase()}`,
        body.mode === 'live' ? 'Desde ahora las nuevas evaluaciones envían la carta. Nada se envió de forma retroactiva.' : MODE_UI[body.mode].text,
      )
    })
  }

  trackByVersion(_index: number, item: { version: number }): number {
    return item.version
  }

  private afterSave(response: SnapshotResponse<PreofferSettingsOverview>, summary: string, detail: string): void {
    if (response.error) {
      //* 409: otra persona la cambió al mismo tiempo, o ya estaba en ese modo. Se recarga para ver cómo quedó.
      const conflict = response.status === 409
      this.messages.add({ key: 'preoffer-settings', severity: conflict ? 'warn' : 'error', summary: conflict ? 'La configuración cambió' : 'No se pudo guardar', detail: response.msg, life: 6000 })
      if (conflict) this.load()
    } else {
      this.messages.add({ key: 'preoffer-settings', severity: 'success', summary, detail, life: 5000 })
      this.apply(response.data!)
    }
    this.cd.markForCheck()
  }

  private apply(overview: PreofferSettingsOverview): void {
    this.overview = overview
    this.form = thresholdsOf(overview.active)
    this.thresholdsReason = ''
    this.showThresholdErrors = false
    this.modeTarget = overview.active.mode
    this.modeReason = ''
    this.acknowledged = false
  }
}
